import { getContract, resolveNetworkContract } from "../actions/getContract.ts";
import type { Client } from "../clients/types.ts";
import { Pc } from "../postconditions/index.ts";
import { POX_ABI } from "./abi.ts";
import { POX_CONTRACTS } from "./constants.ts";
import type {
	DelegateStxParams,
	DelegationInfo,
	PoxInfo,
	StackExtendParams,
	StackIncreaseParams,
	StackStxParams,
	StackerInfo,
} from "./types.ts";
import { parseBtcAddress, validateLockPeriod } from "./utils.ts";

function getPoxContract(client: Client) {
	const contract = resolveNetworkContract(client, POX_CONTRACTS);
	return getContract({
		client,
		address: contract.address,
		name: contract.name,
		abi: POX_ABI,
	});
}

/** Query current PoX network info (cycle, minimum, lengths). */
export async function getPoxInfo(client: Client): Promise<PoxInfo> {
	const pox = getPoxContract(client);
	return (await pox.read.getPoxInfo({})) as PoxInfo;
}

/** Get stacker info for an address. Returns null if not stacking. */
export async function getStackerInfo(
	client: Client,
	address: string,
): Promise<StackerInfo | null> {
	const pox = getPoxContract(client);
	return (await pox.read.getStackerInfo({
		stacker: address,
	})) as StackerInfo | null;
}

/** Get delegation info for an address. Returns null if not delegating. */
export async function getDelegationInfo(
	client: Client,
	address: string,
): Promise<DelegationInfo | null> {
	const pox = getPoxContract(client);
	return (await pox.read.getDelegationInfo({
		stacker: address,
	})) as DelegationInfo | null;
}

/** Check if an amount meets the minimum stacking threshold. */
export async function canStack(
	client: Client,
	amount: bigint,
): Promise<boolean> {
	const info = await getPoxInfo(client);
	return amount >= info.minAmountUstx;
}

/** Lock STX for stacking (solo). Requires wallet client. */
export async function stackStx(
	client: Client,
	params: StackStxParams,
): Promise<string> {
	if (!client.account) throw new Error("Wallet client required");

	if (!validateLockPeriod(params.lockPeriod)) {
		throw new Error("Lock period must be 1-12 cycles");
	}

	const info = await getPoxInfo(client);
	if (params.amount < info.minAmountUstx) {
		throw new Error(`Amount below minimum: ${info.minAmountUstx}`);
	}

	const poxAddr = parseBtcAddress(params.btcAddress);
	const pox = getPoxContract(client);

	return pox.call.stackStx(
		{
			amountUstx: params.amount,
			poxAddr,
			startBurnHt: params.startBurnHeight,
			lockPeriod: BigInt(params.lockPeriod),
			signerSig: params.signerSig,
			signerKey: params.signerKey,
			maxAmount: params.maxAmount,
			authId: params.authId,
		},
		{
			postConditions: [
				Pc.principal(client.account.address).willSendEq(params.amount).ustx(),
			],
		},
	);
}

/** Delegate STX to a pool operator. Requires wallet client. */
export async function delegateStx(
	client: Client,
	params: DelegateStxParams,
): Promise<string> {
	if (!client.account) throw new Error("Wallet client required");

	const poxAddr = params.poxAddr ? parseBtcAddress(params.poxAddr) : null;
	const pox = getPoxContract(client);

	return pox.call.delegateStx({
		amountUstx: params.amount,
		delegateTo: params.delegateTo,
		untilBurnHt: params.untilBurnHeight ?? null,
		poxAddr,
	});
}

/** Revoke an active delegation. Requires wallet client. */
export async function revokeDelegateStx(client: Client): Promise<string> {
	if (!client.account) throw new Error("Wallet client required");
	const pox = getPoxContract(client);
	return pox.call.revokeDelegateStx({});
}

/** Extend an active stacking lock. Requires wallet client. */
export async function stackExtend(
	client: Client,
	params: StackExtendParams,
): Promise<string> {
	if (!client.account) throw new Error("Wallet client required");

	if (!validateLockPeriod(params.extendCount)) {
		throw new Error("Extend count must be 1-12 cycles");
	}

	const poxAddr = parseBtcAddress(params.btcAddress);
	const pox = getPoxContract(client);

	return pox.call.stackExtend({
		extendCount: BigInt(params.extendCount),
		poxAddr,
		signerSig: params.signerSig,
		signerKey: params.signerKey,
		maxAmount: params.maxAmount,
		authId: params.authId,
	});
}

/** Increase the amount of locked STX. Requires wallet client. */
export async function stackIncrease(
	client: Client,
	params: StackIncreaseParams,
): Promise<string> {
	if (!client.account) throw new Error("Wallet client required");

	const pox = getPoxContract(client);

	return pox.call.stackIncrease(
		{
			increaseBy: params.increaseBy,
			signerSig: params.signerSig,
			signerKey: params.signerKey,
			maxAmount: params.maxAmount,
			authId: params.authId,
		},
		{
			postConditions: [
				Pc.principal(client.account.address)
					.willSendEq(params.increaseBy)
					.ustx(),
			],
		},
	);
}
